import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Shuffle, RotateCcw, Sparkles } from 'lucide-react';
import { getMockSessions, updateMockSession } from '../mock';

const Lottery = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [currentDraw, setCurrentDraw] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const sessions = getMockSessions();
    const found = sessions.find(s => s.id === id);
    if (found) {
      setSession(found);
    } else {
      navigate('/');
    }
  }, [id, navigate]);

  const playSound = (type) => {
    // Create simple beep sound using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (type === 'draw') {
      oscillator.frequency.value = 800;
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } else if (type === 'complete') {
      oscillator.frequency.value = 600;
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.value = 800;
        gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.3);
      }, 150);
    }
  };

  const handleDraw = () => {
    if (session.remainingNumbers.length === 0 || isDrawing) return;
    
    setIsDrawing(true);
    playSound('draw');
    
    // Animation: show random numbers for effect
    let count = 0;
    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * session.remainingNumbers.length);
      setCurrentDraw(session.remainingNumbers[randomIndex]);
      count++;
      
      if (count >= 10) {
        clearInterval(interval);
        
        // Actually draw the number
        const finalIndex = Math.floor(Math.random() * session.remainingNumbers.length);
        const drawnNumber = session.remainingNumbers[finalIndex];
        const newRemaining = [...session.remainingNumbers];
        newRemaining.splice(finalIndex, 1);
        
        const updatedSession = {
          ...session,
          drawnNumbers: [...session.drawnNumbers, drawnNumber],
          remainingNumbers: newRemaining,
          completed: newRemaining.length === 0
        };
        
        setCurrentDraw(drawnNumber);
        setSession(updatedSession);
        updateMockSession(id, updatedSession);
        
        if (newRemaining.length === 0) {
          playSound('complete');
        }
        
        setTimeout(() => setIsDrawing(false), 500);
      }
    }, 100);
  };

  if (!session) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali
          </Button>
          
          <Button
            variant="outline"
            onClick={() => navigate('/sessions')}
            className="flex items-center gap-2"
          >
            Lihat Semua Sesi
          </Button>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
            Undian Lot #{session.setupNumber}
          </h1>
          <p className="text-gray-600">Sisa: {session.remainingNumbers.length} nomor</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Draw Section */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-orange-600" />
                Nomor Terpilih
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-center h-48 bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100 rounded-xl relative overflow-hidden">
                {currentDraw !== null ? (
                  <div className={`text-8xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent ${isDrawing ? 'animate-bounce' : 'animate-in zoom-in duration-500'}`}>
                    {currentDraw}
                  </div>
                ) : (
                  <p className="text-gray-400 text-lg">Tekan tombol untuk mulai</p>
                )}
              </div>
              
              <Button
                onClick={handleDraw}
                disabled={session.remainingNumbers.length === 0 || isDrawing}
                className="w-full h-16 text-xl bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 disabled:opacity-50 transition-all duration-300"
              >
                <Shuffle className="w-6 h-6 mr-2" />
                {isDrawing ? 'Mengundi...' : 'Tarik Lot'}
              </Button>
              
              {session.completed && (
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full h-12 border-2 flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Mulai Undian Baru
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Remaining Numbers */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle>Nomor Tersisa ({session.remainingNumbers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {session.remainingNumbers.length > 0 ? (
                <div className="flex flex-wrap gap-2 max-h-80 overflow-y-auto p-2">
                  {session.remainingNumbers.map((num, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="text-lg px-4 py-2 bg-gradient-to-r from-orange-50 to-pink-50 border-orange-300 hover:scale-110 transition-transform duration-200"
                    >
                      {num}
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-2xl font-bold text-green-600 mb-2">Selesai!</p>
                  <p className="text-gray-600">Semua nomor telah diundi</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Lottery;